import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import NoticeBanner from '@/components/NoticeBanner';
import Seo from '@/components/Seo';
import { optimizeImage } from '@/utils/imageOptimize';
import ProtectedImage from '@/components/ProtectedImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_MAP = {
  '1 Negro': '#000000',
  '1A Castaño Oscuro Ceniza': '#2C2419',
  '1B Castaño Oscuro': '#3D2817',
  '2 Castaño Medio': '#5C4033',
  '3 Castaño Claro': '#73503C',
  '4 Castaño Más Claro': '#8B6F47',
  '6 Rubio Ceniza': '#A89968',
  '7 Rubio Marrón': '#9A7D54',
  '8 Rubio Medio': '#C9A961',
  '18 Rubio Muy Claro': '#E6D7A3'
};

const GRAY_MAP = {
  '0%': '#000000', '10%': '#1A1A1A', '20%': '#333333', '30%': '#4D4D4D',
  '40%': '#666666', '50%': '#808080', '60%': '#999999', '70%': '#B3B3B3', '80%': '#CCCCCC'
};

export default function ProductDetail({ cartCount, setCartCount }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState({});
  const [currentPrice, setCurrentPrice] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => { fetchProduct(); }, [id]);
  useEffect(() => { if (product) setCurrentPrice(product.price); }, [product]);

  const fetchProduct = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products/${id}`);
      const data = await response.json();
      setProduct(data);
    } catch (error) { console.error('Error:', error); }
  };

  const handleVariantChange = (key, value) => {
    const newVariant = {...selectedVariant, [key]: value};
    setSelectedVariant(newVariant);
    if (product.variant_prices && value && product.variant_prices[value]) {
      setCurrentPrice(product.variant_prices[value]);
    }
  };

  const variantLabel = (key) => {
    const map = {
      size: t('product_detail.size'), format: t('product_detail.format'),
      type: t('product_detail.type'), option: t('product_detail.option'),
      color: t('product_detail.color'), gray_percentage: t('product_detail.grey_percentage'),
      density: t('product_detail.density'), texture: t('product_detail.texture'),
      etiqueta: 'Etiquetado'
    };
    return map[key] || key.replace('_', ' ');
  };

  const addToCart = async () => {
    // Defensive client-side guard (server also validates)
    if (product.track_stock !== false && product.stock !== null && product.stock !== undefined && product.stock < quantity) {
      toast.error(`Stock insuficiente (disponibles: ${product.stock})`);
      return;
    }
    const sessionId = localStorage.getItem('sessionId');
    try {
      await fetch(`${BACKEND_URL}/api/cart?session_id=${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: product.id, quantity, price: currentPrice, product_name: product.name, variant: product.has_variants ? selectedVariant : null })
      });
      setCartCount(cartCount + quantity);
      toast.success(t('product_detail.product_added'));
    } catch (error) { toast.error(t('common.error')); }
  };

  if (!product) return <div className="min-h-screen flex items-center justify-center">{t('product_detail.loading')}</div>;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": product.name,
    "description": product.description,
    "image": product.images?.[0] || '',
    "brand": { "@type": "Brand", "name": product.brand || "Arthur Fitz" },
    "sku": product.sku || product.id,
    "offers": {
      "@type": "Offer",
      "url": window.location.href,
      "priceCurrency": "EUR",
      "price": currentPrice,
      "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      "seller": { "@type": "Organization", "name": "Arthur Fitz" },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "ES" },
        "deliveryTime": { "@type": "ShippingDeliveryTime", "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 2, "unitCode": "DAY" }, "transitTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 3, "unitCode": "DAY" } }
      }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={product.name}
        description={(product.description || '').slice(0, 160) || `${product.name} disponible en Arthur Fitz. Envío a toda España.`}
        path={`/producto/${product.id}`}
        image={product.images && product.images[0]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: product.name,
          description: product.description || '',
          image: product.images || [],
          brand: { '@type': 'Brand', name: product.brand || 'Arthur Fitz' },
          sku: product.sku || product.id,
          offers: {
            '@type': 'Offer',
            url: `https://arthurfitz.com/producto/${product.id}`,
            priceCurrency: 'EUR',
            price: product.price,
            availability: product.stock > 0 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            itemCondition: 'https://schema.org/NewCondition',
          },
        }}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ARTHUR FITZ</Link>
          <Link to="/carrito" className="relative" data-testid="cart-link-product">
            <ShoppingCart className="w-6 h-6" />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{cartCount}</span>
            )}
          </Link>
        </div>
      </header>
      <NoticeBanner />
      <div className="container mx-auto px-6 py-12 max-w-6xl pb-32 md:pb-12">
        <Link to="/productos" className="text-sm mb-6 inline-block hover:underline" data-testid="back-link">← {t('product_detail.back')}</Link>
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <div className="bg-gray-100 aspect-square mb-4">
              <ProtectedImage
                src={optimizeImage(product.images[selectedImageIndex] || product.images[0], 'detail')}
                alt={product.name}
                className="w-full h-full"
                loading="eager"
              />
            </div>
            {product.images.length > 1 && (
              <div className="flex gap-2">
                {product.images.map((image, index) => (
                  <button key={image || `img-${index}`} onClick={() => setSelectedImageIndex(index)} className={`w-20 h-20 border-2 transition-all ${selectedImageIndex === index ? 'border-black' : 'border-gray-300'}`}>
                    <ProtectedImage
                      src={optimizeImage(image, 'thumb')}
                      alt={`${product.name} ${index + 1}`}
                      className="w-full h-full"
                      watermark={false}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-6">
            <div>
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>{product.name}</h1>
              <p className="text-gray-600 mb-4">{product.description}</p>
              <div className="text-3xl font-bold mb-6">{currentPrice.toFixed(2)}€</div>
            </div>
            {product.has_variants && product.variant_options && (
              <div className="space-y-4 border-t border-gray-200 pt-6">
                <h3 className="font-bold">{t('product_detail.customize')}</h3>
                {Object.keys(product.variant_options).map((key) => (
                  <div key={key}>
                    <label className="block text-sm font-medium mb-2 capitalize">{variantLabel(key)}</label>
                    {key === 'etiqueta' ? (
                      <div className="space-y-2">
                        {product.variant_options[key].map((option) => (
                          <label key={option} className={`flex items-start gap-3 p-3 border-2 rounded cursor-pointer transition-colors ${selectedVariant[key] === option ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-400'}`}>
                            <input type="radio" name="etiqueta" value={option} checked={selectedVariant[key] === option} onChange={() => handleVariantChange(key, option)} className="mt-0.5" />
                            <div>
                              <span className="font-medium text-sm">{option}</span>
                              {option.toLowerCase().includes('sin etiqueta') && (
                                <p className="text-xs text-gray-500 mt-1">Para tu privacidad en el hogar. El producto se envía sin etiqueta identificativa de la marca.</p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                    <Select onValueChange={(value) => handleVariantChange(key, value)}>
                      <SelectTrigger className="border-gray-300">
                        <SelectValue placeholder={`${t('product_detail.select')} ${key === 'gray_percentage' ? t('product_detail.grey') : key}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {product.variant_options[key].map((option) => (
                          <SelectItem key={option} value={option}>
                            <div className="flex items-center gap-2">
                              {key === 'color' && COLOR_MAP[option] && (<div className="w-5 h-5 rounded-full border-2 border-gray-300" style={{ backgroundColor: COLOR_MAP[option] }} />)}
                              {key === 'gray_percentage' && GRAY_MAP[option] && (<div className="w-5 h-5 rounded-full border-2 border-gray-300" style={{ backgroundColor: GRAY_MAP[option] }} />)}
                              <span>{option}{product.variant_prices && product.variant_prices[option] && ` - ${product.variant_prices[option].toFixed(2)}€`}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-medium mb-2">{t('product_detail.quantity')}</label>
              <div className="flex items-center gap-4 mb-6">
                <Button variant="outline" size="icon" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="border-gray-300"><Minus className="w-4 h-4" /></Button>
                <span className="text-xl font-bold w-12 text-center">{quantity}</span>
                <Button variant="outline" size="icon" onClick={() => setQuantity(quantity + 1)} className="border-gray-300"><Plus className="w-4 h-4" /></Button>
              </div>
              <Button
                onClick={addToCart}
                disabled={product.track_stock !== false && !product.allow_backorder && (product.stock === 0 || product.stock === undefined ? false : product.stock < quantity)}
                className="w-full bg-black text-white hover:bg-gray-800 py-6 text-base font-medium disabled:bg-gray-300 disabled:cursor-not-allowed"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {product.track_stock !== false && product.stock === 0 && !product.allow_backorder
                  ? 'Agotado'
                  : product.allow_backorder && product.stock === 0
                    ? 'Reservar (pre-venta)'
                    : t('product_detail.add_to_cart')}
              </Button>
              {product.allow_backorder && product.availability_date && (
                <div className="mt-3 p-3 bg-orange-50 border-2 border-orange-300 rounded text-sm" data-testid="backorder-notice">
                  <p className="font-bold text-orange-800">📦 Pre-venta</p>
                  <p className="text-orange-700 mt-1">
                    Producto {product.stock === 0 ? 'agotado' : 'con stock limitado'}. Tu pedido se enviará el <strong>{new Date(product.availability_date).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
                  </p>
                </div>
              )}
              {product.track_stock !== false && product.stock !== null && product.stock !== undefined && product.stock > 0 && product.stock <= 5 && !product.allow_backorder && (
                <p className="text-xs text-orange-600 mt-2 text-center" data-testid="low-stock-warning">
                  ¡Quedan solo {product.stock} unidades!
                </p>
              )}
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600" data-testid="discreet-shipping-notice">
                <p className="font-medium text-gray-800 mb-1">Envío 100% discreto</p>
                <p>Todos nuestros pedidos se envían en embalaje neutro, sin publicidad, sin marcas visibles y sin remite que identifique el contenido.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-only sticky Add-to-Cart bar. Keeps the price + primary CTA
          reachable while the user scrolls through variants and details. */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t-2 border-black shadow-[0_-4px_16px_rgba(0,0,0,0.08)] safe-bottom"
        data-testid="mobile-add-to-cart-bar"
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-gray-500 leading-none">Precio</p>
            <p className="text-lg font-bold text-black leading-tight">{currentPrice.toFixed(2)}€</p>
          </div>
          <Button
            onClick={addToCart}
            disabled={product.track_stock !== false && !product.allow_backorder && (product.stock === 0 || product.stock === undefined ? false : product.stock < quantity)}
            className="flex-1 bg-black text-white hover:bg-gray-800 h-12 text-sm font-semibold disabled:bg-gray-300"
            data-testid="mobile-add-to-cart-btn"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {product.track_stock !== false && product.stock === 0 && !product.allow_backorder
              ? 'Agotado'
              : product.allow_backorder && product.stock === 0
                ? 'Reservar'
                : t('product_detail.add_to_cart')}
          </Button>
        </div>
      </div>
    </div>
  );
}
